'use client';

import { AlertTriangle, Shield, TrendingUp, Info } from 'lucide-react';
import { useState } from 'react';
import { calculateRiskMatrix, formatEPSSScore, type RiskQuadrant } from '../utils/epssApi';

interface RiskMatrixProps {
  cvssScore: number;
  epssScore: number;
  className?: string;
}

export default function RiskMatrix({ cvssScore, epssScore, className = '' }: RiskMatrixProps) {
  const [showHelp, setShowHelp] = useState(false);
  const riskResult = calculateRiskMatrix(cvssScore, epssScore);

  return (
    <div className={`p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Risk Prioritization Matrix
        </h3>
        <button
          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 relative"
          onClick={() => setShowHelp(!showHelp)}
          onBlur={() => setShowHelp(false)}
          aria-label="Risk matrix help"
        >
          <Info className="w-5 h-5" />
          {showHelp && (
            <div className="absolute right-0 top-8 w-80 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 text-left">
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                The Risk Matrix combines <strong>CVSS</strong> (severity) with <strong>EPSS</strong>{' '}
                (exploitation probability) to help prioritize remediation efforts.
              </p>
              <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-2">
                <li><strong>Q1 - Critical:</strong> High severity, actively exploited</li>
                <li><strong>Q2 - Monitor:</strong> High severity, low exploitation</li>
                <li><strong>Q3 - Urgent:</strong> Lower severity, actively exploited</li>
                <li><strong>Q4 - Scheduled:</strong> Lower severity, low exploitation</li>
              </ul>
            </div>
          )}
        </button>
      </div>

      {/* Current Position Indicator */}
      <div className={`p-4 rounded-lg ${riskResult.bgColor} mb-4`}>
        <div className="flex items-center gap-3">
          <AlertTriangle className={`w-6 h-6 ${riskResult.color}`} />
          <div>
            <div className={`text-lg font-bold ${riskResult.color}`}>
              {riskResult.label}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {riskResult.description}
            </div>
          </div>
        </div>
      </div>

      {/* Visual Matrix */}
      <div className="grid grid-cols-2 gap-1 mb-4">
        {/* Q1 - High CVSS, High EPSS */}
        <QuadrantCell
          quadrant="Q1"
          label="Critical"
          isActive={riskResult.quadrant === 'Q1'}
          position="top-left"
        />
        {/* Q2 - High CVSS, Low EPSS */}
        <QuadrantCell
          quadrant="Q2"
          label="Monitor"
          isActive={riskResult.quadrant === 'Q2'}
          position="top-right"
        />
        {/* Q3 - Low CVSS, High EPSS */}
        <QuadrantCell
          quadrant="Q3"
          label="Urgent"
          isActive={riskResult.quadrant === 'Q3'}
          position="bottom-left"
        />
        {/* Q4 - Low CVSS, Low EPSS */}
        <QuadrantCell
          quadrant="Q4"
          label="Scheduled"
          isActive={riskResult.quadrant === 'Q4'}
          position="bottom-right"
        />
      </div>

      {/* Axis Labels */}
      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-2">
        <div className="flex items-center gap-1">
          <TrendingUp className="w-3 h-3" />
          <span>High EPSS ({'>'}30%)</span>
        </div>
        <div className="flex items-center gap-1">
          <TrendingUp className="w-3 h-3 opacity-50" />
          <span>Low EPSS ({'<'}30%)</span>
        </div>
      </div>

      {/* Score Summary */}
      <div className="flex justify-between items-center pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">CVSS:</span>
          <span className="font-bold">{cvssScore.toFixed(1)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">EPSS:</span>
          <span className="font-bold">{formatEPSSScore(epssScore)}</span>
        </div>
      </div>
    </div>
  );
}

interface QuadrantCellProps {
  quadrant: RiskQuadrant;
  label: string;
  isActive: boolean;
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

function QuadrantCell({ quadrant, label, isActive, position }: QuadrantCellProps) {
  const baseClasses = 'p-3 rounded text-center transition-all';

  const getQuadrantColors = () => {
    switch (quadrant) {
      case 'Q1':
        return isActive
          ? 'bg-red-500 text-white ring-2 ring-red-600 ring-offset-2 dark:ring-offset-gray-800'
          : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
      case 'Q2':
        return isActive
          ? 'bg-orange-500 text-white ring-2 ring-orange-600 ring-offset-2 dark:ring-offset-gray-800'
          : 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300';
      case 'Q3':
        return isActive
          ? 'bg-yellow-700 text-white ring-2 ring-yellow-700 ring-offset-2 dark:ring-offset-gray-800'
          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      case 'Q4':
        return isActive
          ? 'bg-green-700 text-white ring-2 ring-green-600 ring-offset-2 dark:ring-offset-gray-800'
          : 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
    }
  };

  const getPositionLabel = () => {
    switch (position) {
      case 'top-left':
        return 'High Sev / High Exp';
      case 'top-right':
        return 'High Sev / Low Exp';
      case 'bottom-left':
        return 'Low Sev / High Exp';
      case 'bottom-right':
        return 'Low Sev / Low Exp';
    }
  };

  return (
    <div className={`${baseClasses} ${getQuadrantColors()}`}>
      <div className="font-bold text-sm">{label}</div>
      <div className={`text-xs ${isActive ? 'opacity-90' : 'opacity-70'}`}>
        {getPositionLabel()}
      </div>
      {isActive && (
        <div className="mt-1 text-xs font-medium">
          Current Position
        </div>
      )}
    </div>
  );
}
